import { useCallback, useEffect, useRef, useState } from 'react'
import { IconButton } from './IconButton'
import styles from './Lightbox.module.css'

/** One picture in a lightbox: what to show, and what to call it. */
export interface LightboxImage {
  /** The full-size image. Callers pass the original, not the thumbnail they showed. */
  src: string
  /** Alt text, and — for the image on screen — the dialog's accessible name. */
  alt: string
  /** Shown under the picture. The image's title, usually. */
  caption?: string | undefined
}

export interface LightboxProps {
  /**
   * The set to page through. A single-image lightbox is a one-element set, so the sheet's
   * portrait and the gallery's grid open the same component rather than two that drift.
   */
  images: readonly LightboxImage[]
  /** Which one opens. Out-of-range values are clamped rather than blanking the screen. */
  index?: number
  onClose: () => void
}

/**
 * One image at a time, drawn over everything, at the largest size the screen allows.
 *
 * A portrait on a sheet is a 120px tile and a gallery thumbnail is smaller — enough to identify
 * a picture, not enough to look at one. This is where looking happens, so it is deliberately
 * chrome-free: a scrim, the picture fitted to the viewport, and the controls needed to move.
 *
 * **Moving between images is the point on a phone.** Opening a gallery image and having to
 * close, aim at the next thumbnail and open again is four gestures to see the next picture. So
 * a horizontal swipe pages, as it does in every photo app; arrow keys do the same on a
 * keyboard, and there are real buttons for both because a swipe is invisible and a mouse cannot
 * make one. The set wraps — a gallery is a loop, and a dead end at either edge is a gesture
 * that silently does nothing.
 *
 * Escape closes, and so does a click on the scrim; a click on the picture itself does not,
 * because the picture is the thing the user came to touch and drag around.
 */
export function Lightbox({ images, index = 0, onClose }: LightboxProps) {
  const count = images.length
  const [current, setCurrent] = useState(() => clamp(index, count))

  // Following the prop matters when the caller changes which image is open while it is open —
  // opening a different tile without closing first, for instance.
  useEffect(() => setCurrent(clamp(index, count)), [index, count])

  const step = useCallback(
    (delta: number) => {
      if (count < 2) return
      // `+ count` because JS `%` keeps the sign, so a step back from 0 would land at -1.
      setCurrent((position) => (position + delta + count) % count)
    },
    [count],
  )

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') step(1)
      if (event.key === 'ArrowLeft') step(-1)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, step])

  // The page behind must not scroll under the image — on a phone a drag on the scrim would
  // otherwise move the sheet, and closing would land the reader somewhere else.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const swipe = useSwipe(step)

  const image = images[current]
  if (image === undefined) return null

  const label = image.alt === '' ? 'Image' : image.alt

  return (
    <div className={styles.scrim} onClick={onClose}>
      <div
        className={styles.frame}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={(event) => event.stopPropagation()}
        {...swipe}
      >
        {/* `draggable={false}` so a mouse drag across the picture does not start a browser
            image-drag instead of doing nothing, which is what a reader expects. */}
        <img className={styles.image} src={image.src} alt={image.alt} draggable={false} />

        <IconButton
          label="Close"
          icon="close"
          className={styles.close}
          autoFocus
          onClick={onClose}
        />

        {count > 1 && (
          <>
            <IconButton
              label="Previous image"
              icon="prev"
              className={`${styles.step} ${styles.stepPrev}`}
              onClick={() => step(-1)}
            />
            <IconButton
              label="Next image"
              icon="next"
              className={`${styles.step} ${styles.stepNext}`}
              onClick={() => step(1)}
            />
          </>
        )}

        {(count > 1 || image.caption !== undefined) && (
          <div className={styles.bar}>
            {image.caption !== undefined && <span className={styles.caption}>{image.caption}</span>}
            {/* `role="status"` so paging is announced: the picture changes and nothing else does. */}
            {count > 1 && (
              <span className={styles.counter} role="status">
                {current + 1} of {count}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function clamp(index: number, count: number): number {
  if (count === 0) return 0
  return Math.min(Math.max(index, 0), count - 1)
}

/**
 * Horizontal swipe, in the smallest form that works.
 *
 * Touch handlers rather than pointer events with capture: this needs to know where a finger
 * started and where it left, and nothing in between. The thresholds are the ones that keep a
 * swipe from firing on a tap (48px) and from firing on a vertical scroll that drifted sideways
 * (the horizontal distance must beat the vertical one).
 */
function useSwipe(step: (delta: number) => void) {
  const start = useRef<{ x: number; y: number } | null>(null)

  return {
    onTouchStart: (event: React.TouchEvent) => {
      const touch = event.touches[0]
      start.current = touch === undefined ? null : { x: touch.clientX, y: touch.clientY }
    },
    onTouchEnd: (event: React.TouchEvent) => {
      const from = start.current
      const touch = event.changedTouches[0]
      start.current = null

      if (from === null || touch === undefined) return

      const dx = touch.clientX - from.x
      const dy = touch.clientY - from.y

      if (Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy)) return

      // Swiping left moves forward, the direction the picture itself appears to travel.
      step(dx < 0 ? 1 : -1)
    },
  }
}
